import React, { useState, useEffect, useCallback } from 'react';
import { getStaff, setAdminStatus, setMythicalStatus, deleteStaffAccount, getQuickLinks, createQuickLink, updateQuickLink, deleteQuickLink, reorderQuickLinks, getAnnouncements, reorderPinnedAnnouncements, getWhoami, getOfficeNetworks, addOfficeNetwork, removeOfficeNetwork } from '../api/apiClient';
import socket from '../socket';
import AdminAnalytics from './AdminAnalytics';
import AdminActivityLog from './AdminActivityLog';
import AdminBugReports from './AdminBugReports';
import { ICONS, ICON_KEYS, SECTION_ORDER, SECTION_LABELS, BUILTIN_MARKETING } from './QuickLinks';
import './AdminSettings.css';

// ── Staff tab ─────────────────────────────────────────────────────────────────
const StaffTab = ({ currentUser }) => {
  const [staff,      setStaff]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [busyId,     setBusyId]     = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [search,     setSearch]     = useState('');

  const fetchStaff = useCallback(async () => {
    try {
      const res = await getStaff();
      setStaff(res.data);
    } catch (err) {
      console.error('Error loading staff:', err);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchStaff();
    socket.on('staff:changed', fetchStaff);
    return () => socket.off('staff:changed', fetchStaff);
  }, [fetchStaff]);

  const handleToggleAdmin = async (member) => {
    setBusyId(member.id);
    try {
      await setAdminStatus(member.id, { is_admin: !member.is_admin, requester_id: currentUser.id });
      setStaff(prev => prev.map(s => s.id === member.id ? { ...s, is_admin: s.is_admin ? 0 : 1 } : s));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update admin status');
    } finally { setBusyId(null); }
  };

  // Am I (the logged-in user) a mythical admin? Derived from the fetched list so
  // it doesn't depend on the shape of the currentUser object.
  const iAmMythical = staff.some(s => s.id === currentUser.id && s.is_mythical_admin);

  const handleToggleMythical = async (member) => {
    setBusyId(member.id);
    try {
      await setMythicalStatus(member.id, { is_mythical_admin: !member.is_mythical_admin });
      setStaff(prev => prev.map(s => s.id === member.id ? { ...s, is_mythical_admin: !s.is_mythical_admin } : s));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update Mythical Admin status');
    } finally { setBusyId(null); }
  };

  const handleDelete = async (id) => {
    setBusyId(id); setConfirmDel(null);
    try {
      await deleteStaffAccount(id, currentUser.id);
      setStaff(prev => prev.filter(s => s.id !== id));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete account');
    } finally { setBusyId(null); }
  };

  const filtered = staff
    .filter(s =>
      s.name.toLowerCase().includes(search.toLowerCase()) ||
      s.email.toLowerCase().includes(search.toLowerCase())
    )
    // Admins first, then by name
    .sort((a, b) => (b.is_admin === 1) - (a.is_admin === 1) || a.name.localeCompare(b.name));

  return (
    <>
      <div className="adm-search-bar">
        <input
          type="text"
          placeholder="Search by name or email…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="adm-search"
        />
        <span className="adm-count">{filtered.length} account{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      <div className="adm-body">
        {loading ? (
          <p className="adm-loading">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="adm-loading">No accounts found.</p>
        ) : (
          <ul className="adm-list">
            {filtered.map(member => {
              const isSelf     = member.id === currentUser.id;
              const isAdmin    = member.is_admin === 1;
              const isMythical = !!member.is_mythical_admin;
              const busy       = busyId === member.id;
              const initials = member.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
              return (
                <li key={member.id} className={`adm-row ${isSelf ? 'adm-self' : ''}`}>
                  <div className="adm-avatar">
                    {member.avatar
                      ? <img src={member.avatar} alt={member.name} />
                      : <span>{initials}</span>}
                  </div>
                  <div className="adm-info">
                    <span className="adm-name">
                      {member.name}
                      {isSelf && <span className="adm-you">You</span>}
                      {isAdmin && <span className="adm-badge">Admin</span>}
                      {isMythical && <span className="adm-badge adm-badge-mythical" title="Can run Apollo enrichment on either CRM">✨ Mythical</span>}
                    </span>
                    <span className="adm-email">{member.email}</span>
                  </div>
                  {!isSelf && (
                    <div className="adm-actions">
                      {iAmMythical && (
                        <button
                          className={`adm-btn adm-btn-mythical ${isMythical ? 'demote' : 'promote'}`}
                          onClick={() => handleToggleMythical(member)}
                          disabled={busy}
                          title="Mythical Admins can run Apollo enrichment on either CRM"
                        >
                          {busy ? '…' : isMythical ? 'Remove Mythical' : '✨ Make Mythical'}
                        </button>
                      )}
                      <button
                        className={`adm-btn adm-btn-admin ${isAdmin ? 'demote' : 'promote'}`}
                        onClick={() => handleToggleAdmin(member)}
                        disabled={busy}
                      >
                        {busy ? '…' : isAdmin ? 'Remove Admin' : 'Make Admin'}
                      </button>
                      {confirmDel === member.id ? (
                        <div className="adm-confirm">
                          <span>Delete?</span>
                          <button className="adm-btn adm-btn-danger-confirm" onClick={() => handleDelete(member.id)} disabled={busy}>Yes</button>
                          <button className="adm-btn adm-btn-cancel" onClick={() => setConfirmDel(null)}>No</button>
                        </div>
                      ) : (
                        <button
                          className="adm-btn adm-btn-danger"
                          onClick={() => setConfirmDel(member.id)}
                          disabled={busy}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </>
  );
};

// ── Quick Links tab ───────────────────────────────────────────────────────────
const EMPTY_QL_FORM = { title: '', category: SECTION_ORDER[0], icon: ICON_KEYS[0], url: '', options: [] };

const QuickLinksTab = () => {
  const [links,      setLinks]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [form,       setForm]       = useState(EMPTY_QL_FORM);
  const [mode,       setMode]       = useState('link'); // 'link' | 'dropdown'
  const [editingId,  setEditingId]  = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState('');
  const [dragId,     setDragId]     = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  const fetchLinks = useCallback(async () => {
    try {
      const res = await getQuickLinks();
      setLinks(res.data);
    } catch { } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchLinks();
    socket.on('links:changed', fetchLinks);
    return () => socket.off('links:changed', fetchLinks);
  }, [fetchLinks]);

  const resetForm = () => {
    setForm(EMPTY_QL_FORM);
    setMode('link');
    setEditingId(null);
    setError('');
  };

  const startEdit = (link) => {
    const hasOptions = link.options && link.options.length > 0;
    setMode(hasOptions ? 'dropdown' : 'link');
    setForm({
      title: link.title,
      category: link.category,
      icon: link.icon || ICON_KEYS[0],
      url: link.url || '',
      options: hasOptions ? link.options.map(o => ({ label: o.label, url: o.url })) : [],
    });
    setEditingId(link.id);
    setError('');
  };

  const addOptionRow = () => setForm(f => ({ ...f, options: [...f.options, { label: '', url: '' }] }));
  const removeOptionRow = (i) => setForm(f => ({ ...f, options: f.options.filter((_, idx) => idx !== i) }));
  const updateOptionRow = (i, key, val) => setForm(f => ({
    ...f,
    options: f.options.map((o, idx) => idx === i ? { ...o, [key]: val } : o),
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { setError('Name is required.'); return; }

    let options = [];
    if (mode === 'dropdown') {
      options = form.options.map(o => ({ label: o.label.trim(), url: o.url.trim() })).filter(o => o.label && o.url);
      if (options.length === 0) { setError('Add at least one option, or switch to Single Link.'); return; }
    } else if (!form.url.trim()) {
      setError('URL is required.'); return;
    }

    setSaving(true); setError('');
    try {
      const payload = {
        title: form.title.trim(),
        category: form.category,
        icon: form.icon,
        url: mode === 'link' ? form.url.trim() : null,
        options,
        position: editingId ? undefined : links.length,
      };
      if (editingId) await updateQuickLink(editingId, payload);
      else await createQuickLink(payload);
      resetForm();
      fetchLinks();
    } catch {
      setError(editingId ? 'Failed to save changes.' : 'Failed to add link.');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await deleteQuickLink(id);
      if (editingId === id) resetForm();
      fetchLinks();
    } catch { alert('Failed to delete link.'); }
  };

  const handleDragStart = (e, linkId) => {
    setDragId(linkId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, linkId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (linkId !== dragId) setDragOverId(linkId);
  };

  const handleDrop = async (e, targetLink) => {
    e.preventDefault();
    if (!dragId || dragId === targetLink.id) { setDragId(null); setDragOverId(null); return; }
    const draggedLink = links.find(l => l.id === dragId);
    if (!draggedLink || draggedLink.category !== targetLink.category) { setDragId(null); setDragOverId(null); return; }

    const byCategory = Object.fromEntries(SECTION_ORDER.map(k => [k, links.filter(l => l.category === k)]));
    const cat = draggedLink.category;
    const fromIdx = byCategory[cat].findIndex(l => l.id === dragId);
    const toIdx   = byCategory[cat].findIndex(l => l.id === targetLink.id);
    const reordered = [...byCategory[cat]];
    reordered.splice(toIdx, 0, reordered.splice(fromIdx, 1)[0]);
    byCategory[cat] = reordered;

    const allReordered = SECTION_ORDER.flatMap(k => byCategory[k]);
    setLinks(allReordered);
    setDragId(null);
    setDragOverId(null);

    try {
      await reorderQuickLinks(allReordered.map((l, i) => ({ id: l.id, position: i })));
    } catch { fetchLinks(); }
  };

  const handleDragEnd = () => { setDragId(null); setDragOverId(null); };

  const grouped = SECTION_ORDER.map(key => ({
    key, label: SECTION_LABELS[key], items: links.filter(l => l.category === key),
  }));

  return (
    <div className="adm-ql-wrap">
      <div className="adm-ql-add-card">
        <h3 className="adm-ql-add-title">{editingId ? 'Edit Quick Link' : 'Add Quick Link'}</h3>
        {error && <p className="adm-ql-error">{error}</p>}
        <form onSubmit={handleSubmit} className="adm-ql-form">
          <div className="adm-ql-row">
            <div className="adm-ql-field">
              <label>Button Name</label>
              <input
                type="text"
                placeholder="e.g. Clio"
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              />
            </div>
            <div className="adm-ql-field">
              <label>Section</label>
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                {SECTION_ORDER.map(key => <option key={key} value={key}>{SECTION_LABELS[key]}</option>)}
              </select>
            </div>
          </div>

          <div className="adm-ql-field">
            <label>Icon</label>
            <div className="adm-ql-icon-grid">
              {ICON_KEYS.map(key => (
                <button
                  key={key}
                  type="button"
                  title={key}
                  className={`adm-ql-icon-tile${form.icon === key ? ' adm-ql-icon-tile--selected' : ''}`}
                  onClick={() => setForm(f => ({ ...f, icon: key }))}
                >
                  {ICONS[key]}
                </button>
              ))}
            </div>
          </div>

          <div className="adm-ql-mode-toggle">
            <button
              type="button"
              className={`adm-ql-mode-btn ${mode === 'link' ? 'adm-ql-mode-btn--active' : ''}`}
              onClick={() => setMode('link')}
            >
              Single Link
            </button>
            <button
              type="button"
              className={`adm-ql-mode-btn ${mode === 'dropdown' ? 'adm-ql-mode-btn--active' : ''}`}
              onClick={() => setMode('dropdown')}
            >
              Dropdown
            </button>
          </div>

          {mode === 'link' ? (
            <div className="adm-ql-field">
              <label>URL</label>
              <input
                type="text"
                placeholder="https://… or /internal-path"
                value={form.url}
                onChange={e => setForm(f => ({ ...f, url: e.target.value }))}
              />
            </div>
          ) : (
            <div className="adm-ql-options-builder">
              {form.options.map((opt, i) => (
                <div key={i} className="adm-ql-option-row">
                  <input
                    type="text"
                    placeholder="Label (e.g. PDF)"
                    value={opt.label}
                    onChange={e => updateOptionRow(i, 'label', e.target.value)}
                  />
                  <input
                    type="text"
                    placeholder="https://… or /internal-path"
                    value={opt.url}
                    onChange={e => updateOptionRow(i, 'url', e.target.value)}
                  />
                  <button type="button" className="adm-ql-option-remove" onClick={() => removeOptionRow(i)} aria-label="Remove option">×</button>
                </div>
              ))}
              <button type="button" className="adm-ql-option-add" onClick={addOptionRow}>+ Add Option</button>
            </div>
          )}

          <div className="adm-ql-form-actions">
            <button type="submit" className="adm-ql-add-btn" disabled={saving}>
              {saving ? 'Saving…' : editingId ? 'Save Changes' : '+ Add Link'}
            </button>
            {editingId && (
              <button type="button" className="adm-btn adm-btn-cancel" onClick={resetForm}>Cancel</button>
            )}
          </div>
        </form>
      </div>

      {loading ? (
        <p className="adm-loading">Loading…</p>
      ) : (
        grouped.map(section => {
          const builtins = section.key === 'marketing' ? BUILTIN_MARKETING : [];
          if (!section.items.length && !builtins.length) return null;
          return (
            <div key={section.key} className="adm-ql-section">
              <h4 className="adm-ql-section-title">{section.label}</h4>
              <ul className="adm-ql-list">
                {builtins.map(app => (
                  <li key={app.path} className="adm-ql-item adm-ql-item--builtin">
                    <span className="adm-ql-drag-handle adm-ql-drag-handle--disabled">
                      <svg width="12" height="16" viewBox="0 0 12 16" fill="currentColor">
                        <circle cx="4" cy="3" r="1.4"/><circle cx="8" cy="3" r="1.4"/>
                        <circle cx="4" cy="8" r="1.4"/><circle cx="8" cy="8" r="1.4"/>
                        <circle cx="4" cy="13" r="1.4"/><circle cx="8" cy="13" r="1.4"/>
                      </svg>
                    </span>
                    <span className="adm-ql-item-icon">{ICONS[app.icon] || ICONS.folder}</span>
                    <div className="adm-ql-item-info">
                      <span className="adm-ql-item-name">{app.title}</span>
                      <span className="adm-ql-item-url">{app.path}</span>
                    </div>
                    <div className="adm-actions">
                      <span className="adm-ql-builtin-badge">Built-in</span>
                    </div>
                  </li>
                ))}
                {section.items.map(link => (
                  <li
                    key={link.id}
                    className={`adm-ql-item${dragId === link.id ? ' adm-ql-item--dragging' : ''}${dragOverId === link.id ? ' adm-ql-item--dragover' : ''}`}
                    draggable
                    onDragStart={e => handleDragStart(e, link.id)}
                    onDragOver={e => handleDragOver(e, link.id)}
                    onDrop={e => handleDrop(e, link)}
                    onDragEnd={handleDragEnd}
                  >
                    <span className="adm-ql-drag-handle" title="Drag to reorder">
                      <svg width="12" height="16" viewBox="0 0 12 16" fill="currentColor">
                        <circle cx="4" cy="3" r="1.4"/><circle cx="8" cy="3" r="1.4"/>
                        <circle cx="4" cy="8" r="1.4"/><circle cx="8" cy="8" r="1.4"/>
                        <circle cx="4" cy="13" r="1.4"/><circle cx="8" cy="13" r="1.4"/>
                      </svg>
                    </span>
                    <span className="adm-ql-item-icon">{ICONS[link.icon] || ICONS.folder}</span>
                    <div className="adm-ql-item-info">
                      <span className="adm-ql-item-name">{link.title}</span>
                      <span className="adm-ql-item-url">
                        {link.options && link.options.length > 0
                          ? link.options.map(o => o.label).join(' / ')
                          : link.url}
                      </span>
                    </div>
                    <div className="adm-actions">
                      <button className="adm-btn adm-btn-admin promote" onClick={() => startEdit(link)}>Edit</button>
                      <button className="adm-btn adm-btn-danger" onClick={() => handleDelete(link.id)}>Remove</button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          );
        })
      )}
    </div>
  );
};

// ── Pinned Messages tab ───────────────────────────────────────────────────────
const PinnedTab = () => {
  const [pinned,     setPinned]     = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [dragId,     setDragId]     = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  const fetchPinned = useCallback(async () => {
    try {
      const res = await getAnnouncements();
      setPinned((res.data || []).filter(a => a.pinned));
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchPinned();
    socket.on('announcements:changed', fetchPinned);
    return () => socket.off('announcements:changed', fetchPinned);
  }, [fetchPinned]);

  const handleDragStart = (e, id) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, id) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (id !== dragId) setDragOverId(id);
  };

  const handleDrop = async (e, targetId) => {
    e.preventDefault();
    if (!dragId || dragId === targetId) { setDragId(null); setDragOverId(null); return; }
    const fromIdx = pinned.findIndex(a => a.id === dragId);
    const toIdx   = pinned.findIndex(a => a.id === targetId);
    const reordered = [...pinned];
    reordered.splice(toIdx, 0, reordered.splice(fromIdx, 1)[0]);
    setPinned(reordered);
    setDragId(null);
    setDragOverId(null);
    try {
      await reorderPinnedAnnouncements(reordered.map((a, i) => ({ id: a.id, pin_order: i })));
    } catch { fetchPinned(); }
  };

  const handleDragEnd = () => { setDragId(null); setDragOverId(null); };

  return (
    <div className="adm-ql-wrap">
      {loading ? (
        <p className="adm-loading">Loading…</p>
      ) : pinned.length === 0 ? (
        <p className="adm-loading">No pinned announcements. Pin announcements from the announcements page.</p>
      ) : (
        <div className="adm-ql-section">
          <h4 className="adm-ql-section-title">Drag to set display order</h4>
          <ul className="adm-ql-list">
            {pinned.map(a => (
              <li
                key={a.id}
                className={`adm-ql-item${dragId === a.id ? ' adm-ql-item--dragging' : ''}${dragOverId === a.id ? ' adm-ql-item--dragover' : ''}`}
                draggable
                onDragStart={e => handleDragStart(e, a.id)}
                onDragOver={e => handleDragOver(e, a.id)}
                onDrop={e => handleDrop(e, a.id)}
                onDragEnd={handleDragEnd}
              >
                <span className="adm-ql-drag-handle" title="Drag to reorder">
                  <svg width="12" height="16" viewBox="0 0 12 16" fill="currentColor">
                    <circle cx="4" cy="3" r="1.4"/><circle cx="8" cy="3" r="1.4"/>
                    <circle cx="4" cy="8" r="1.4"/><circle cx="8" cy="8" r="1.4"/>
                    <circle cx="4" cy="13" r="1.4"/><circle cx="8" cy="13" r="1.4"/>
                  </svg>
                </span>
                <span className="adm-ql-item-icon">📌</span>
                <div className="adm-ql-item-info">
                  <span className="adm-ql-item-name">{a.title}</span>
                  <span className="adm-ql-item-url">by {a.author_name}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
// ── Office WiFi tab ─────────────────────────────────────────────────────────────
const OfficeWifiTab = () => {
  const [nets, setNets]   = useState([]);
  const [myIp, setMyIp]   = useState('');
  const [ip, setIp]       = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy]   = useState(false);
  const [err, setErr]     = useState('');

  const load = useCallback(() => {
    getOfficeNetworks().then(r => setNets(r.data || [])).catch(() => {});
    getWhoami().then(r => setMyIp(r.data.ip || '')).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (payload) => {
    setBusy(true); setErr('');
    try {
      await addOfficeNetwork(payload);
      setIp(''); setLabel('');
      load();
    } catch (e) { setErr(e.response?.data?.error || 'Could not add network'); }
    finally { setBusy(false); }
  };

  const remove = async (target) => {
    if (!window.confirm(`Remove ${target} from office networks?`)) return;
    await removeOfficeNetwork(target).catch(() => {});
    load();
  };

  const alreadyHasMyIp = nets.some(n => n.ip === myIp);

  const addManual = () => { if (ip.trim() && !busy) add({ ip: ip.trim(), label: label.trim() }); };

  return (
    <div className="owf">
      <div className="owf-current">
        <div className="owf-current-info">
          <span className="owf-current-label">Your current network IP</span>
          <span className="owf-current-ip">{myIp || '…'}</span>
        </div>
        <button
          className="owf-btn owf-btn--primary"
          disabled={busy || !myIp || alreadyHasMyIp}
          onClick={() => add({ use_current: true, label: 'Added from this device' })}
        >
          {alreadyHasMyIp ? '✓ Already an office network' : 'Set current network as office'}
        </button>
      </div>

      <div className="owf-head">
        <h3 className="owf-h3">Office networks</h3>
        <span className="owf-count">{nets.length}</span>
      </div>
      <p className="owf-hint">Add every public IP the office exits through (e.g. a second circuit or an IPv6 address). Anyone on one of these shows as In the office.</p>

      <div className="owf-add">
        <input className="owf-input" placeholder="IP address (e.g. 38.104.180.210)" value={ip}
          onChange={e => setIp(e.target.value)} onKeyDown={e => e.key === 'Enter' && addManual()} />
        <input className="owf-input owf-input--label" placeholder="Label (optional)" value={label}
          onChange={e => setLabel(e.target.value)} onKeyDown={e => e.key === 'Enter' && addManual()} />
        <button className="owf-btn owf-btn--primary" disabled={busy || !ip.trim()} onClick={addManual}>Add</button>
      </div>
      {err && <p className="owf-err">{err}</p>}

      <ul className="owf-list">
        {nets.length === 0 && <li className="owf-empty">No office networks yet — add one above.</li>}
        {nets.map(n => (
          <li key={n.ip} className="owf-row">
            <div className="owf-row-info">
              <span className="owf-row-ip">{n.ip}{n.ip === myIp && <span className="owf-here">You're on this now</span>}</span>
              <span className="owf-row-label">{n.label || 'Office network'}</span>
            </div>
            <button className="owf-btn owf-btn--danger" onClick={() => remove(n.ip)}>Remove</button>
          </li>
        ))}
      </ul>
    </div>
  );
};

const AdminSettings = ({ currentUser }) => {
  const [tab, setTab] = useState('staff');

  return (
    <div className="adm-page">
      <div className="adm-page-header">
        <h2>Admin Settings</h2>
      </div>

      <div className="adm-tabs">
        <button
          className={`adm-tab ${tab === 'staff' ? 'adm-tab--active' : ''}`}
          onClick={() => setTab('staff')}
        >
          Staff Accounts
        </button>
        <button
          className={`adm-tab ${tab === 'links' ? 'adm-tab--active' : ''}`}
          onClick={() => setTab('links')}
        >
          Quick Links
        </button>
        <button
          className={`adm-tab ${tab === 'pinned' ? 'adm-tab--active' : ''}`}
          onClick={() => setTab('pinned')}
        >
          Pinned Posts
        </button>
        <button
          className={`adm-tab ${tab === 'analytics' ? 'adm-tab--active' : ''}`}
          onClick={() => setTab('analytics')}
        >
          Analytics
        </button>
        <button
          className={`adm-tab ${tab === 'logs' ? 'adm-tab--active' : ''}`}
          onClick={() => setTab('logs')}
        >
          Activity Logs
        </button>
        <button
          className={`adm-tab ${tab === 'bugs' ? 'adm-tab--active' : ''}`}
          onClick={() => setTab('bugs')}
        >
          Bug Reports
        </button>
        <button
          className={`adm-tab ${tab === 'office' ? 'adm-tab--active' : ''}`}
          onClick={() => setTab('office')}
        >
          Office WiFi
        </button>
      </div>

      <div className="adm-tab-body">
        {tab === 'staff' && <StaffTab currentUser={currentUser} />}
        {tab === 'links' && <QuickLinksTab />}
        {tab === 'pinned' && <PinnedTab />}
        {tab === 'analytics' && <AdminAnalytics currentUser={currentUser} />}
        {tab === 'logs' && <AdminActivityLog currentUser={currentUser} />}
        {tab === 'bugs' && <AdminBugReports />}
        {tab === 'office' && <OfficeWifiTab />}
      </div>
    </div>
  );
};

export default AdminSettings;
